import { cn } from '@/lib/utils'
import { useLogoutMutation } from '@/services/api/authApi'
import { useGetMyProfile } from '@/services/api/userApi'
import { useAppSelector } from '@/store/hooks'
import { Menu, X } from 'lucide-react'
import { useState } from 'react'
import { Link, useLocation } from 'react-router-dom'
import { Avatar, AvatarFallback, AvatarImage } from '../ui/avatar'
import { Button } from '../ui/button'
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger } from '../ui/dropdown-menu'
import { CustomNavLink } from './CustomNavLink'

const Navbar = () => {
  const { session } = useAppSelector((state) => state.auth)
  const { data: profile } = useGetMyProfile()
  const location = useLocation()
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false)

  const { mutate: logout } = useLogoutMutation()

  const handleLogout = async () => {
    logout()
  }

  const isLoggedIn = !!session?.user

  const closeMobileMenu = () => {
    setIsMobileMenuOpen(false)
  }

  const navigationItems: { to: string; label: string }[] = [
    { to: '/lists', label: 'Browse Lists' },
    { to: '/profiles', label: 'Collectors' },
  ]

  if (isLoggedIn) {
    navigationItems.push({ to: '/my-lists', label: 'My Lists' })
  }

  return (
    <nav className="bg-background/95 backdrop-blur">
      <div className="container mx-auto px-4">
        <div className="relative flex h-16 items-center justify-between">
          <div className="flex items-center">
            <Link
              to="/"
              className="text-xl font-bold bg-gradient-to-r from-indigo-600 to-purple-600 bg-clip-text text-transparent"
            >
              KinzShip
            </Link>
          </div>

          {/* Desktop Navigation */}
          <div className="hidden md:flex md:items-center md:space-x-4">
            {navigationItems.map((item) => (
              <CustomNavLink key={item.to} to={item.to} currentPath={location.pathname}>
                {item.label}
              </CustomNavLink>
            ))}
          </div>

          <div className="flex items-center space-x-4">
            {/* Mobile menu button */}
            <Button
              variant="ghost"
              size="icon"
              className="md:hidden"
              onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
            >
              {isMobileMenuOpen ? <X className="h-6 w-6" /> : <Menu className="h-6 w-6" />}
            </Button>

            {/* User menu */}
            {isLoggedIn ? (
              <DropdownMenu>
                <DropdownMenuTrigger asChild>
                  <Avatar className="h-8 w-8 cursor-pointer border">
                    <AvatarImage src={profile?.avatar_url || ''} />
                    <AvatarFallback>
                      {profile?.display_name?.charAt(0) || profile?.email?.charAt(0) || ''}
                    </AvatarFallback>
                  </Avatar>
                </DropdownMenuTrigger>
                <DropdownMenuContent align="end">
                  <DropdownMenuItem asChild>
                    <Link to="/settings" className="w-full">
                      Settings
                    </Link>
                  </DropdownMenuItem>
                  <DropdownMenuItem className="cursor-pointer" onClick={handleLogout}>
                    Logout
                  </DropdownMenuItem>
                </DropdownMenuContent>
              </DropdownMenu>
            ) : (
              <>
                <Button variant="outline" asChild>
                  <Link to="/register">Register</Link>
                </Button>
                <Button asChild>
                  <Link to="/login">Login</Link>
                </Button>
              </>
            )}
          </div>
        </div>

        {/* Mobile Navigation */}
        <div
          className={cn(
            'absolute left-0 right-0 z-20 bg-background border-b md:hidden',
            isMobileMenuOpen ? 'block' : 'hidden',
          )}
        >
          <div className="space-y-1 px-2 pb-3 pt-2">
            {navigationItems.map((item) => (
              <CustomNavLink key={item.to} to={item.to} currentPath={location.pathname} onClick={closeMobileMenu}>
                {item.label}
              </CustomNavLink>
            ))}
          </div>
        </div>
      </div>
    </nav>
  )
}

export default Navbar
